/**
 * @author claude
 * @date 2026.03.04
 * @description 상품별 판매 통계 페이지
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMagnifyingGlass} from '@fortawesome/free-solid-svg-icons';
import {toast} from 'react-toastify';
import * as XLSX from 'xlsx';

import DatePickerInput from '../../form/DatePickerInput';
import Pagination from '../../layout/Pagination';
import {useProductStatisticsStore} from '../../../store/useProductStatistics';
import {formatDate, getStartDateByPeriod, PeriodType, updateUrlWithParams} from '../../../util/util';

const toLocalDateString = (date: Date): string => {
    return formatDate(date)['yyyy-mm-dd'];
};

// yy.MM.dd 형식으로 날짜 포맷
const toShortDateString = (dateStr: string): string => {
    const date = new Date(dateStr);
    const yy = String(date.getFullYear()).slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}.${mm}.${dd}`;
};

type TabType = 'product' | 'daily';

const ProductStatistics = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState<
        'week' | 'month' | '90days' | 'year'
    >('month');
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<TabType>('product');
    const [pageSize, setPageSize] = useState<number>(20);

    const {
        getProductStatistics,
        productStatistics,
        getProductStatisticsLoading,
        productSalesDetail,
        productSalesDetailPage,
        getProductSalesDetail,
        getProductSalesDetailLoading,
    } = useProductStatisticsStore();

    const memoizedDates = useMemo(
        () => ({
            todayDate: toLocalDateString(new Date()),
            thirtyDaysAgoDate: toLocalDateString(
                (() => {
                    const date = new Date();
                    date.setDate(date.getDate() - 30);
                    return date;
                })(),
            ),
        }),
        [],
    );

    const [startDate, setStartDate] = useState<Date | null>(() => {
        const currentQuery = queryString.parse(location.search);
        if (currentQuery.startDate) {
            return new Date(currentQuery.startDate as string);
        }
        const today = new Date();
        return new Date(today.setDate(today.getDate() - 30));
    });

    const [endDate, setEndDate] = useState<Date | null>(() => {
        const currentQuery = queryString.parse(location.search);
        if (currentQuery.endDate) {
            return new Date(currentQuery.endDate as string);
        }
        return new Date();
    });

    // 현재 URL에서 날짜 파라미터 가져오기
    const currentDateParams = useMemo(() => {
        const parsedQuery = queryString.parse(location.search);
        return {
            startDate: (parsedQuery.startDate as string) || memoizedDates.thirtyDaysAgoDate,
            endDate: (parsedQuery.endDate as string) || memoizedDates.todayDate,
        };
    }, [location.search, memoizedDates]);

    // URL 쿼리 동기화
    useEffect(() => {
        const currentQuery = queryString.parse(location.search);
        if (currentQuery.startDate) {
            setStartDate(new Date(currentQuery.startDate as string));
        }
        if (currentQuery.endDate) {
            setEndDate(new Date(currentQuery.endDate as string));
        }
    }, [location.search]);

    // URL 변경 감지하여 summary 데이터 로드
    useEffect(() => {
        getProductStatistics({
            startDate: currentDateParams.startDate,
            endDate: currentDateParams.endDate,
        });
    }, [currentDateParams.startDate, currentDateParams.endDate, getProductStatistics]);

    // 탭 전환 시 일자별 데이터가 없으면 조회
    useEffect(() => {
        if (activeTab === 'daily' && productSalesDetail.length === 0 && !getProductSalesDetailLoading) {
            getProductSalesDetail({
                startDate: currentDateParams.startDate,
                endDate: currentDateParams.endDate,
                page: 0,
                size: pageSize,
            });
        }
    }, [activeTab, productSalesDetail.length, getProductSalesDetailLoading, currentDateParams, getProductSalesDetail, pageSize]);

    const handleSearch = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            const queryParams = {
                startDate: startDate
                    ? toLocalDateString(startDate)
                    : memoizedDates.thirtyDaysAgoDate,
                endDate: endDate
                    ? toLocalDateString(endDate)
                    : memoizedDates.todayDate,
            };

            updateUrlWithParams(queryParams, navigate);
        },
        [startDate, endDate, navigate, memoizedDates],
    );

    // 상품별 통계 엑셀 다운로드
    // 기간 탭 선택 시 해당 기간의 날짜 범위로 데이터 조회
    const handlePeriodChange = useCallback(
        (period: PeriodType) => {
            setSelectedPeriod(period);

            const today = new Date();
            const start = getStartDateByPeriod(period);

            const queryParams = {
                startDate: toLocalDateString(start),
                endDate: toLocalDateString(today),
            };

            setStartDate(start);
            setEndDate(today);
            updateUrlWithParams(queryParams, navigate);
        },
        [navigate],
    );

    // 날짜별 펼치기/접기 토글
    const toggleDateExpansion = useCallback((e: React.MouseEvent, date: string) => {
        e.preventDefault();
        e.stopPropagation();

        setExpandedDates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(date)) {
                newSet.delete(date);
            } else {
                newSet.add(date);
            }
            return newSet;
        });
    }, []);

    // 전체 펼치기/접기
    const toggleAllExpansion = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (expandedDates.size === productSalesDetail.length) {
                setExpandedDates(new Set());
            } else {
                setExpandedDates(new Set(productSalesDetail.map(stat => stat.confirmDt)));
            }
        },
        [expandedDates.size, productSalesDetail],
    );

    // 일자별 페이지 변경
    const handleDetailPageChange = useCallback(
        (newPage: number) => {
            setExpandedDates(new Set());
            getProductSalesDetail({
                startDate: currentDateParams.startDate,
                endDate: currentDateParams.endDate,
                page: newPage,
                size: pageSize,
            });
        },
        [currentDateParams, getProductSalesDetail, pageSize],
    );

    // 페이지 사이즈 변경
    const handlePageSizeChange = useCallback(
        (newSize: number) => {
            setPageSize(newSize);
            setExpandedDates(new Set());
            getProductSalesDetail({
                startDate: currentDateParams.startDate,
                endDate: currentDateParams.endDate,
                page: 0,
                size: newSize,
            });
        },
        [currentDateParams, getProductSalesDetail],
    );

    // 탭 전환
    const handleTabChange = useCallback((tab: TabType) => {
        setActiveTab(tab);
        setExpandedDates(new Set());
    }, []);

    // 요약 통계 계산
    const summary = useMemo(() => {
        const totalOrderCount = productStatistics.reduce(
            (sum, item) => sum + (item.orderCount || 0),
            0,
        );
        const totalSubtotal = productStatistics.reduce(
            (sum, item) => sum + (item.subtotal || 0),
            0,
        );

        return {totalOrderCount, totalSubtotal};
    }, [productStatistics]);

    // 상품별 통계 엑셀 다운로드
    const handleProductExcelDownload = useCallback(() => {
        if (productStatistics.length === 0) {
            toast.error('다운로드할 데이터가 없습니다.');
            return;
        }

        const rows = productStatistics.map(item => ({
            '상품ID': item.productId,
            '상품명': item.productName,
            '단가': item.price,
            '주문건수': item.orderCount,
            '소계': item.subtotal,
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '상품별 주문 통계');
        XLSX.writeFile(wb, `상품별_주문통계_${currentDateParams.startDate}_${currentDateParams.endDate}.xlsx`);
    }, [productStatistics, summary, currentDateParams]);

    // 일자별 상세 내역 합계
    const detailSummary = useMemo(() => {
        const totalCount = productSalesDetail.reduce(
            (sum, item) => sum + (item.totalCount || 0),
            0,
        );
        const totalPendingCount = productSalesDetail.reduce(
            (sum, item) => sum + (item.totalPendingCount || 0),
            0,
        );
        const totalSubtotal = productSalesDetail.reduce(
            (sum, item) =>
                sum +
                item.products.reduce((s, p) => s + (p.subtotal || 0), 0),
            0,
        );
        return {totalCount, totalPendingCount, totalSubtotal};
    }, [productSalesDetail]);

    // 검색된 날짜 범위 표시 문자열
    const dateRangeLabel = useMemo(() => {
        return `${toShortDateString(currentDateParams.startDate)} ~ ${toShortDateString(currentDateParams.endDate)}`;
    }, [currentDateParams]);

    // 기간 라벨
    const periodLabel = useMemo(() => {
        switch (selectedPeriod) {
            case 'week':
                return '주간';
            case 'month':
                return '월간';
            case '90days':
                return '90일간';
            case 'year':
                return '연간';
        }
    }, [selectedPeriod]);

    return (
        <div className="!flex !flex-col !h-full !gap-6 !p-6">
            {/* 필터 영역 */}
            <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-4">
                <div className="!w-full">
                    <div className="!flex !justify-between !items-center">
                        <button
                            onClick={() => setIsFilterOpen(prev => !prev)}
                            className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
                        >
                            {isFilterOpen ? '닫기' : '검색필터 열기'}
                        </button>
                    </div>
                    <form
                        onSubmit={handleSearch}
                        className={`
              ${isFilterOpen ? '!flex' : '!hidden'}
              md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center md:!justify-between !w-full
            `}
                    >
                        <div className="!flex !items-center !gap-4 !flex-wrap">
              <span className="!text-lg !font-semibold !text-gray-800">
                상품별 판매 통계
              </span>
                            <div className="!flex !items-center !gap-1">
                                <DatePickerInput
                                    value={startDate}
                                    onChange={(date: Date | null) => setStartDate(date)}
                                    placeholder="시작일"
                                    width={130}
                                />
                                <span className="!text-gray-400">~</span>
                                <DatePickerInput
                                    value={endDate}
                                    onChange={(date: Date | null) => setEndDate(date)}
                                    placeholder="종료일"
                                    width={130}
                                />
                            </div>
                            <button
                                type="submit"
                                className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-700 !text-white !rounded-lg !text-sm !font-medium !flex !items-center !gap-2 !transition-colors"
                            >
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className="!text-sm"
                                />
                                검색
                            </button>
                            <button
                                type="button"
                                onClick={handleProductExcelDownload}
                                className="!px-4 !py-2 !bg-green-500 !text-white !rounded-lg hover:!bg-green-600 !transition-colors !text-sm !font-medium"
                            >
                                엑셀 다운로드
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* 하단 테이블 영역 */}
            <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6">
                {/* 탭 버튼 */}
                <div className="!flex !gap-2 !mb-4">
                    {[
                        {key: 'product' as const, label: '상품별 주문 통계'},
                        {key: 'daily' as const, label: '일자별 주문 통계'},
                    ].map(({key, label}) => (
                        <button
                            key={key}
                            onClick={e => {
                                e.preventDefault();
                                handleTabChange(key);
                            }}
                            className={`!px-4 !py-2 !rounded-lg !text-sm !font-medium !transition-colors ${
                                activeTab === key
                                    ? '!bg-blue-500 !text-white'
                                    : '!bg-gray-100 !text-gray-700 hover:!bg-gray-200'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* 상품별 주문 통계 탭 */}
                {activeTab === 'product' && (
                    <>
                        <div className="!flex !justify-between !items-center !mb-4">
                            <h3 className="!text-lg !font-semibold !text-gray-800">
                                상품별 주문 통계
                                {productStatistics.length > 0 && (
                                    <span className="!text-sm !font-normal !text-gray-500 !ml-2">
                    (총 {productStatistics.length}개 상품)
                  </span>
                                )}
                            </h3>
                        </div>
                        {getProductStatisticsLoading ? (
                            <div className="!flex !justify-center !items-center !py-8">
                                <div
                                    className="!animate-spin !rounded-full !h-8 !w-8 !border-b-2 !border-blue-500"></div>
                            </div>
                        ) : productStatistics.length > 0 ? (
                            <div className="!overflow-x-auto">
                                <table className="!w-full !text-sm">
                                    <thead>
                                    <tr className="!bg-gray-50 !border-b !border-gray-200">
                                        <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[120px]">
                                            상품ID
                                        </th>
                                        <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[120px]">
                                            상품명
                                        </th>
                                        <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[100px]">
                                            단가
                                        </th>
                                        <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[80px]">
                                            주문건수
                                        </th>
                                        <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[100px]">
                                            소계
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {productStatistics.map((item, index) => (
                                        <tr
                                            key={index}
                                            className="!border-b !border-gray-100 hover:!bg-gray-50"
                                        >
                                            <td className="!px-4 !py-3 !text-gray-500 !text-xs !font-mono">
                                                {item.productId}
                                            </td>
                                            <td className="!px-4 !py-3 !text-gray-700">
                                                {item.productName}
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !text-gray-700">
                                                {Number(item.price).toLocaleString()}원
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !text-blue-600">
                                                {Number(item.orderCount).toLocaleString()}건
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !text-amber-600">
                                                {Number(item.subtotal).toLocaleString()}원
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <tfoot>
                                    <tr className="!bg-gray-100 !border-t-2 !border-gray-300">
                                        <td className="!px-4 !py-3" />
                                        <td className="!px-4 !py-3 !font-bold !text-gray-800">
                                            합계
                                        </td>
                                        <td className="!px-4 !py-3 !text-right !text-gray-500">
                                            -
                                        </td>
                                        <td className="!px-4 !py-3 !text-right !font-bold !text-blue-700 !text-base">
                                            {summary.totalOrderCount.toLocaleString()}건
                                        </td>
                                        <td className="!px-4 !py-3 !text-right !font-bold !text-amber-700 !text-base">
                                            {summary.totalSubtotal.toLocaleString()}원
                                        </td>
                                    </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ) : (
                            <div className="!text-center !py-8 !text-gray-500">
                                조회된 통계 데이터가 없습니다.
                            </div>
                        )}
                    </>
                )}

                {/* 일자별 주문 통계 탭 */}
                {activeTab === 'daily' && (
                    <>
                        <div className="!flex !justify-between !items-center !mb-4">
                            <h3 className="!text-lg !font-semibold !text-gray-800">
                                일자별 주문 통계
                                {productSalesDetailPage && (
                                    <span className="!text-sm !font-normal !text-gray-500 !ml-2">
                    (현재 페이지 - 주문 {detailSummary.totalCount.toLocaleString()}건 / 표시행 {productSalesDetail.length}건)
                  </span>
                                )}
                            </h3>
                            <select
                                value={pageSize}
                                onChange={e => handlePageSizeChange(Number(e.target.value))}
                                className="!px-3 !py-1.5 !rounded !border !border-gray-300 !text-sm !text-gray-700 !bg-white hover:!border-gray-400 focus:!outline-none focus:!border-blue-500"
                            >
                                {[
                                    {value: 20, label: '20건'},
                                    {value: 50, label: '50건'},
                                    {value: 100, label: '100건'},
                                    {value: 10000, label: '전체'},
                                ].map(({value, label}) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {getProductSalesDetailLoading ? (
                            <div className="!flex !justify-center !items-center !py-8">
                                <div
                                    className="!animate-spin !rounded-full !h-8 !w-8 !border-b-2 !border-blue-500"></div>
                            </div>
                        ) : productSalesDetail.length > 0 ? (
                            <>
                                <div className="!overflow-x-auto">
                                    <table className="!w-full !text-sm">
                                        <thead>
                                        <tr className="!bg-gray-50 !border-b !border-gray-200">
                                            <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[120px]">
                                                <button
                                                    onClick={toggleAllExpansion}
                                                    className="!flex !items-center !gap-1 hover:!text-blue-600 !transition-colors"
                                                >
                                                    <svg
                                                        className={`!w-4 !h-4 !transition-transform ${
                                                            expandedDates.size === productSalesDetail.length
                                                                ? '!rotate-90'
                                                                : ''
                                                        }`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M9 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                    날짜
                                                </button>
                                            </th>
                                            <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[120px]">
                                                상품명
                                            </th>
                                            <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[100px]">
                                                단가
                                            </th>
                                            <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[80px]">
                                                주문건수
                                            </th>
                                            <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[80px]">
                                                대기건수
                                            </th>
                                            <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700 !min-w-[100px]">
                                                매출액
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {productSalesDetail.map((stat, index) => {
                                            const isExpanded = expandedDates.has(stat.confirmDt);

                                            return (
                                                <React.Fragment key={`group-${index}`}>
                                                    {/* 날짜별 요약 행 */}
                                                    <tr
                                                        className="!border-b !border-gray-200 !bg-gray-50 hover:!bg-gray-100 !cursor-pointer"
                                                        onClick={e => toggleDateExpansion(e, stat.confirmDt)}
                                                    >
                                                        <td className="!px-4 !py-3 !font-medium !text-gray-900">
                                                            <div className="!flex !items-center !gap-2">
                                                                <svg
                                                                    className={`!w-4 !h-4 !transition-transform ${
                                                                        isExpanded ? '!rotate-90' : ''
                                                                    }`}
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M9 5l7 7-7 7"
                                                                    />
                                                                </svg>
                                                                {stat.confirmDt}
                                                            </div>
                                                        </td>
                                                        <td className="!px-4 !py-3 !text-gray-600 !italic">
                                                            {`${stat.products.length}개 상품`}
                                                        </td>
                                                        <td className="!px-4 !py-3 !text-right !text-gray-500">
                                                            -
                                                        </td>
                                                        <td className="!px-4 !py-3 !text-right !font-bold !text-blue-600">
                                                            {(stat.totalCount || 0).toLocaleString()}건
                                                        </td>
                                                        <td className="!px-4 !py-3 !text-right !font-medium !text-orange-500">
                                                            {(stat.totalPendingCount || 0).toLocaleString()}건
                                                        </td>
                                                        <td className="!px-4 !py-3 !text-right !font-bold !text-amber-600">
                                                            {stat.products
                                                                .reduce((sum, p) => sum + (p.subtotal || 0), 0)
                                                                .toLocaleString()}원
                                                        </td>
                                                    </tr>
                                                    {/* 상품별 상세 행 */}
                                                    {isExpanded &&
                                                        stat.products
                                                            .filter(p => p.orderCount > 0)
                                                            .map((prod, prodIndex) => (
                                                                <tr
                                                                    key={`${index}-${prodIndex}`}
                                                                    className="!border-b !border-gray-100 hover:!bg-gray-50"
                                                                >
                                                                    <td className="!px-4 !py-3 !pl-12 !text-gray-400">
                                                                        -
                                                                    </td>
                                                                    <td className="!px-4 !py-3 !text-gray-700">
                                                                        <div>{prod.productName}</div>
                                                                        <div className="!text-xs !text-gray-400">{prod.productId}</div>
                                                                    </td>
                                                                    <td className="!px-4 !py-3 !text-right !text-gray-700">
                                                                        {Number(prod.price).toLocaleString()}원
                                                                    </td>
                                                                    <td className="!px-4 !py-3 !text-right !text-blue-600">
                                                                        {Number(prod.orderCount).toLocaleString()}건
                                                                    </td>
                                                                    <td className="!px-4 !py-3 !text-right !text-orange-400">
                                                                        {(prod.pendingCount || 0).toLocaleString()}건
                                                                    </td>
                                                                    <td className="!px-4 !py-3 !text-right !text-amber-600">
                                                                        {Number(prod.subtotal).toLocaleString()}원
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                </React.Fragment>
                                            );
                                        })}
                                        </tbody>
                                        <tfoot>
                                        <tr className="!bg-gray-100 !border-t-2 !border-gray-300">
                                            <td
                                                colSpan={3}
                                                className="!px-4 !py-3 !font-bold !text-gray-800 !text-right"
                                            >
                                                합계
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !font-bold !text-blue-700 !text-base">
                                                {detailSummary.totalCount.toLocaleString()}건
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !font-bold !text-orange-600 !text-base">
                                                {detailSummary.totalPendingCount.toLocaleString()}건
                                            </td>
                                            <td className="!px-4 !py-3 !text-right !font-bold !text-amber-700 !text-base">
                                                {detailSummary.totalSubtotal.toLocaleString()}원
                                            </td>
                                        </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                {/* 페이지네이션 */}
                                {productSalesDetailPage && productSalesDetailPage.totalPages > 1 && (
                                    <div className="!flex !justify-center !mt-6">
                                        <Pagination
                                            pageNumber={productSalesDetailPage.number}
                                            totalPages={productSalesDetailPage.totalPages}
                                            onPageChange={handleDetailPageChange}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="!text-center !py-8 !text-gray-500">
                                조회된 통계 데이터가 없습니다.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ProductStatistics;
